import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { X, Loader2, SearchX, Plus, Mic, DollarSign, CalendarPlus, ArrowUpRight } from "lucide-react";
import { IconSearch } from "@/components/icons/revqIcons";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useClient } from "@/lib/clientContext";
import { useAuth } from "@/lib/auth";
import { useChartTabs } from "@/lib/chartTabs";
import { titleCaseName, fmtSex, parseDob } from "@/lib/patientFormat";
import ScheduleModal from "@/components/patients/ScheduleModal";

const MIN_Q = 3;
const uuidOf = (p) => p.chart_uuid || p.chart_id;
const nameOf = (p) => titleCaseName(p.full_name || `${p.first_name || ""} ${p.last_name || ""}`.trim()) || "Patient";
const payerOf = (p) => p.insurance?.payer_name || p.insurance?.carrier_name || p.payer || p.payer_name || null;
const initialsOf = (name) => (String(name).split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("") || "PT").toUpperCase();
const fmtDob = (p) => { const d = parseDob(p.dob); return d ? d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : (p.dob || null); };
const toDate = (v) => { if (!v) return null; const d = new Date(/^\d{4}-\d{2}-\d{2}$/.test(v) ? `${v}T00:00:00` : v); return isNaN(d) ? null : d; };
// Last visit = max(roster last_office_visit, latest revQ note date_of_service).
const lastVisitOf = (p, visits) => {
  const ds = [p.last_office_visit, p.last_visit, p.last_visit_date, p.recent_visit_date, visits?.get(uuidOf(p)), p.mrn ? visits?.get(`mrn:${p.mrn}`) : null].map(toDate).filter(Boolean);
  if (!ds.length) return null;
  const d = new Date(Math.max(...ds));
  return d.toLocaleDateString("en-US", d.getFullYear() === new Date().getFullYear() ? { month: "short", day: "numeric" } : { month: "short", day: "numeric", year: "numeric" });
};
async function loadRevqVisits(clientId) {
  const m = new Map();
  const put = (k, v) => { if (k && v && (!m.has(k) || v > m.get(k))) m.set(k, v); };
  for (let skip = 0, total = 1; skip < total && skip < 2000; skip += 500) {
    const { data } = await api.get("/api/edi/note-to-claim/notes", { params: { client_id: String(clientId), limit: 500, skip, view: "summary" } });
    const notes = data?.notes || []; total = data?.total ?? notes.length;
    notes.forEach((n) => { const dos = n.date_of_service; put(n.chart_uuid, dos); if (n.mrn) put(`mrn:${n.mrn}`, dos); });
    if (!notes.length) break;
  }
  return m;
}

const recentKey = (uid, cid) => `revq.recent.${uid || "u"}.${cid || "c"}`;
function readRecent(uid, cid) { try { return JSON.parse(localStorage.getItem(recentKey(uid, cid)) || "[]"); } catch { return []; } }
function pushRecent(uid, cid, p) {
  try {
    const item = { chart_uuid: uuidOf(p), full_name: nameOf(p), dob: p.dob, mrn: p.mrn, insurance: p.insurance, payer_name: payerOf(p) };
    if (!item.chart_uuid) return;
    const cur = readRecent(uid, cid).filter((x) => (x.chart_uuid) !== item.chart_uuid);
    localStorage.setItem(recentKey(uid, cid), JSON.stringify([item, ...cur].slice(0, 5)));
  } catch { /* ignore */ }
}

const CSS = `
.gs-root{position:relative;flex:1;max-width:440px;}
.gs-field{height:36px;display:flex;align-items:center;gap:8px;padding:0 8px 0 12px;border-radius:8px;background:#F6F6F8;border:1px solid #E9E9EC;transition:background .18s,border-color .18s,box-shadow .2s;}
.gs-field.on{background:#fff;border-color:rgba(0,0,0,.14);box-shadow:0 2px 12px rgba(0,0,0,.08);border-bottom-left-radius:0;border-bottom-right-radius:0;}
.gs-field input{flex:1;background:none;border:0;outline:none;font-size:12px;color:#111110;}
.gs-field input::placeholder{color:#aeaeb2;}
.gs-kbhint{display:inline-flex;align-items:center;gap:2px;font-size:9px;font-weight:700;color:#9B9B97;background:#fff;border:1px solid #E7E6E3;border-radius:5px;padding:2px 5px;flex:none;}
.gs-clr{width:20px;height:20px;flex:none;display:flex;align-items:center;justify-content:center;border:0;background:none;border-radius:5px;color:#aeaeb2;cursor:pointer;}
.gs-clr:hover{background:rgba(0,0,0,.06);color:#111110;}
.gs-dropdown{position:fixed;z-index:9999;background:#fff;border:1px solid rgba(20,20,18,.12);border-radius:12px;box-shadow:0 28px 64px -24px rgba(20,20,18,.34),0 8px 20px -14px rgba(20,20,18,.16);overflow:hidden;animation:gsrise .14s cubic-bezier(.3,.7,.3,1);}
@keyframes gsrise{from{opacity:0;transform:translateY(-2px);}to{opacity:1;transform:none;}}
.gs-lab{font-size:9px;font-weight:800;letter-spacing:.07em;text-transform:uppercase;color:#6C6C69;padding:9px 14px 4px;}
.gs-rows{max-height:min(62vh,500px);overflow:auto;}
.gs-row{display:flex;align-items:center;gap:11px;padding:0 14px;height:48px;cursor:pointer;border:0;width:100%;text-align:left;background:none;transition:background .12s;}
.gs-row.sel,.gs-row:hover{background:#F6F5F3;}
.gs-av{width:30px;height:30px;flex:none;border-radius:8px;display:flex;align-items:center;justify-content:center;font-size:10.5px;font-weight:700;color:#5856D6;background:#ecebfb;}
.gs-main{min-width:0;flex:1;}
.gs-nm{display:block;font-size:12.5px;font-weight:600;color:#141412;line-height:1.3;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.gs-sub{display:block;font-size:10.5px;color:#9B9B97;line-height:1.35;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
@media (max-width:560px){.gs-pay{display:none}}
.gs-pay{flex:0 1 190px;min-width:0;font-size:11px;color:#6C6C69;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.gs-pay.none{color:#AEAEB2;}
.gs-last{flex:none;width:104px;text-align:right;font-size:10.5px;color:#9B9B97;white-space:nowrap;}
.gs-last b{display:block;font-size:11px;font-weight:600;color:#6C6C69;}
.gs-qa{display:flex;align-items:center;gap:2px;flex:none;}
.gs-qab{width:32px;height:32px;display:flex;align-items:center;justify-content:center;border:0;background:none;border-radius:8px;color:#6C6C69;cursor:pointer;position:relative;}
.gs-qab:hover{background:rgba(0,0,0,.07);color:#141412;}
.gs-add{display:flex;align-items:center;gap:11px;padding:0 14px;height:52px;width:100%;border:0;background:none;cursor:pointer;text-align:left;border-bottom:1px solid #EFEEEB;}
.gs-add:hover{background:#F6F5F3;}
.gs-addsq{width:30px;height:30px;flex:none;border-radius:8px;background:#141412;color:#fff;display:flex;align-items:center;justify-content:center;}
.gs-addnew{display:block;width:100%;text-align:left;border:0;background:none;cursor:pointer;padding:11px 14px;border-top:1px solid #EFEEEB;font-size:12px;font-weight:600;color:#5856D6;}
.gs-addnew:hover{background:#F6F5F3;}
.gs-msg{padding:20px 16px;text-align:center;color:#9B9B97;font-size:12px;}
.gs-bigadd{display:block;width:calc(100% - 28px);margin:6px 14px 12px;text-align:center;padding:10px;border-radius:9px;background:#141412;color:#fff;font-size:12.5px;font-weight:700;border:0;cursor:pointer;}
.gs-foot{display:flex;align-items:center;gap:14px;padding:8px 14px;border-top:1px solid #EFEEEB;background:#FBFBFA;}
.gs-kb{display:inline-flex;align-items:center;gap:5px;font-size:10px;color:#9B9B97;}
.gs-kb kbd{font-family:inherit;font-size:9px;font-weight:700;color:#6C6C69;background:#fff;border:1px solid #E7E6E3;border-radius:4px;padding:1px 5px;}
.gs-mono{font-family:monospace;}
`;

function PatientRow({ p, sel, onEnter, onHover, onOpen, onDictate, onFinance, onSchedule, idx, visits }) {
  const nav = !!uuidOf(p);
  const sub = [fmtDob(p) ? `DOB ${fmtDob(p)}` : null, p.mrn ? <span key="m" className="gs-mono">MRN {p.mrn}</span> : null].filter(Boolean);
  const last = lastVisitOf(p, visits), payer = payerOf(p);
  const showQa = nav && sel;
  return (
    <div className={`gs-row ${sel ? "sel" : ""}`} data-testid={`gs-result-${idx}`} onMouseEnter={onHover} onClick={() => onEnter(p)}>
      <span className="gs-av">{initialsOf(nameOf(p))}</span>
      <span className="gs-main">
        <span className="gs-nm">{nameOf(p)}</span>
        <span className="gs-sub">{sub.map((s, i) => <React.Fragment key={i}>{i ? " · " : ""}{s}</React.Fragment>)}</span>
      </span>
      {showQa ? (
        <span className="gs-qa" onClick={(e) => e.stopPropagation()} data-testid={`gs-qa-${idx}`}>
          <button className="gs-qab" title="Dictate" data-testid={`gs-dictate-${idx}`} onClick={() => onDictate(p)}><Mic size={15} /></button>
          <button className="gs-qab" title="Finance" data-testid={`gs-finance-${idx}`} onClick={() => onFinance(p)}><DollarSign size={15} /></button>
          <button className="gs-qab" title="Schedule telehealth" data-testid={`gs-schedule-${idx}`} onClick={() => onSchedule(p)}><CalendarPlus size={15} /></button>
          <button className="gs-qab" title="Open chart" data-testid={`gs-openchart-${idx}`} onClick={() => onOpen(p)}><ArrowUpRight size={15} /></button>
        </span>
      ) : (
        <>
          <span className={`gs-pay ${payer ? "" : "none"}`} title={payer || undefined} data-testid={`gs-payer-${idx}`}>{payer || "No payer on file"}</span>
          <span className="gs-last" data-testid={`gs-last-${idx}`}>{last ? <>Last visit<b>{last}</b></> : "No visits yet"}</span>
        </>
      )}
    </div>
  );
}

export default function GlobalSearch() {
  const navigate = useNavigate();
  const { selectedClientId } = useClient();
  const { user } = useAuth();
  const { openChart: openChartTab, openAddPatient } = useChartTabs();
  const uid = user?.id || user?.user_id || user?.email || "u";
  const [q, setQ] = useState("");
  const [focused, setFocused] = useState(false);
  const [status, setStatus] = useState("idle");
  const [results, setResults] = useState([]);
  const [defaultList, setDefaultList] = useState([]);
  const [recent, setRecent] = useState([]);
  const [sel, setSel] = useState(-1);
  const [schedulePatient, setSchedulePatient] = useState(null);
  const [rect, setRect] = useState(null);
  const [visits, setVisits] = useState(null);
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const abortRef = useRef(null);

  const trimmed = q.trim();
  const typing = trimmed.length >= MIN_Q;

  // Anchor the portaled dropdown under the field; keep it synced on scroll/resize.
  useEffect(() => {
    if (!focused) return undefined;
    const measure = () => { const r = rootRef.current?.getBoundingClientRect(); if (r) { const width = Math.min(720, window.innerWidth - 24); setRect({ top: r.bottom + 4, left: Math.max(12, Math.min(r.left, window.innerWidth - width - 12)), width }); } };
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => { window.removeEventListener("resize", measure); window.removeEventListener("scroll", measure, true); };
  }, [focused]);

  // ⌘K / Ctrl+K focuses the field.
  useEffect(() => {
    const onKey = (e) => { if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); inputRef.current?.focus(); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Default list + recent on focus (per client).
  useEffect(() => {
    if (!focused || !selectedClientId) return;
    setRecent(readRecent(uid, selectedClientId));
    if (defaultList.length) return;
    api.get("/api/edi/note-to-claim/pg-patient-search", { params: { client_id: String(selectedClientId) } })
      .then(({ data }) => setDefaultList(data?.results || data?.patients || [])).catch(() => {});
  }, [focused, selectedClientId, uid]); // eslint-disable-line

  // Reset the default-list cache when the client changes.
  useEffect(() => { setDefaultList([]); setVisits(null); }, [selectedClientId]);

  // Latest revQ visit per chart (one notes read per client, cached while the client is selected).
  useEffect(() => {
    if (!focused || !selectedClientId || visits) return;
    loadRevqVisits(selectedClientId).then(setVisits).catch(() => setVisits(new Map()));
  }, [focused, selectedClientId, visits]);

  // Live search.
  useEffect(() => {
    if (!focused || !typing) { setStatus("idle"); setResults([]); return undefined; }
    if (!selectedClientId) { setStatus("idle"); return undefined; }
    setStatus("loading");
    const t = setTimeout(async () => {
      abortRef.current?.abort();
      const ctrl = new AbortController(); abortRef.current = ctrl;
      try {
        const { data } = await api.get("/api/edi/note-to-claim/pg-patient-search", { params: { client_id: String(selectedClientId), q: trimmed }, signal: ctrl.signal });
        setResults(data?.results || data?.patients || []); setStatus("done"); setSel(-1);
      } catch (e) { if (e.name !== "CanceledError" && e.code !== "ERR_CANCELED") { setResults([]); setStatus("done"); } }
    }, 250);
    return () => clearTimeout(t);
  }, [trimmed, typing, focused, selectedClientId]);

  const beforeList = useMemo(() => (recent.length ? recent : defaultList.slice(0, 5)), [recent, defaultList]);
  const activeList = typing ? results : beforeList;

  const close = () => { setFocused(false); };
  const toTab = (p, intent) => {
    const uuid = uuidOf(p);
    if (!uuid) { toast("This patient has no linked chart yet."); return; }
    pushRecent(uid, selectedClientId, p);
    setQ(""); setFocused(false); inputRef.current?.blur();
    if (openChartTab) openChartTab({ uuid, name: nameOf(p), intent });
    else navigate(`/chart?uuid=${encodeURIComponent(uuid)}`);
  };
  const openChart = useCallback((p) => toTab(p, null), [openChartTab, uid, selectedClientId]); // eslint-disable-line
  const dictate = (p) => toTab(p, "dictate");
  const finance = (p) => toTab(p, "finance");
  const schedule = (p) => { setSchedulePatient(p); setFocused(false); };

  const openAdd = (prefill, focus) => { openAddPatient?.({ prefill, focus }); setQ(""); setFocused(false); inputRef.current?.blur(); };
  const addFromText = () => { const parts = trimmed.split(/\s+/); openAdd({ first: parts[0] || "", last: parts.slice(1).join(" ") || "" }, "dob"); };

  const onKeyDown = (e) => {
    if (e.key === "Escape") { e.preventDefault(); inputRef.current?.blur(); setFocused(false); return; }
    const list = activeList;
    if (e.key === "Enter") { const pick = sel >= 0 ? list[sel] : list[0]; if (pick) { e.preventDefault(); openChart(pick); } return; }
    if (!list.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setSel((s) => Math.min((s < 0 ? -1 : s) + 1, list.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setSel((s) => Math.max((s <= 0 ? 0 : s) - 1, 0)); }
  };

  const rowProps = (p, i) => ({ p, idx: i, visits, sel: sel === i, onHover: () => setSel(i), onEnter: openChart, onOpen: openChart, onDictate: dictate, onFinance: finance, onSchedule: schedule });

  return (
    <div className="gs-root" data-testid="global-search" ref={rootRef}>
      <style>{CSS}</style>
      <div className={`gs-field ${focused ? "on" : ""}`}>
        <IconSearch size={16} style={{ flex: "none", color: "#9B9B97" }} />
        <input
          ref={inputRef} value={q}
          onChange={(e) => { setQ(e.target.value); setSel(-1); }}
          onFocus={() => setFocused(true)}
          onClick={() => setFocused(true)}
          onBlur={() => setTimeout(() => setFocused(false), 140)}
          onKeyDown={onKeyDown}
          placeholder="Search patients…" data-testid="global-search-input" autoComplete="off"
        />
        {status === "loading" && typing ? <Loader2 size={14} className="animate-spin" style={{ color: "#9B9B97", flex: "none" }} /> : null}
        {q ? <button className="gs-clr" onMouseDown={(e) => e.preventDefault()} onClick={() => { setQ(""); inputRef.current?.focus(); }} data-testid="global-search-clear" aria-label="Clear"><X size={14} /></button>
          : <span className="gs-kbhint">⌘K</span>}
      </div>

      {focused && rect ? createPortal(
        <div className="gs-dropdown" style={{ top: rect.top, left: rect.left, width: rect.width }} onMouseDown={(e) => e.preventDefault()} data-testid="global-search-dropdown">
          {!typing ? (
            <div className="gs-rows">
              <button className="gs-add" onClick={() => openAdd(null, "first")} data-testid="gs-add-patient">
                <span className="gs-addsq"><Plus size={16} /></span>
                <span className="gs-main"><span className="gs-nm">Add patient</span><span className="gs-sub">Adds a new patient to this client's roster</span></span>
              </button>
              <div className="gs-lab">Recent patients</div>
              {beforeList.length ? beforeList.map((p, i) => <PatientRow key={uuidOf(p) || p.mrn || i} {...rowProps(p, i)} />)
                : <div className="gs-msg">No recent patients yet.</div>}
            </div>
          ) : (
            <>
              <div className="gs-lab">Patients</div>
              {status === "done" && results.length === 0 ? (
                <div>
                  <div className="gs-msg"><SearchX size={18} style={{ margin: "0 auto 6px" }} />No patient matches “{trimmed}”.</div>
                  <button className="gs-bigadd" onClick={addFromText} data-testid="gs-add-from-text">+ Add “{trimmed}” as a new patient</button>
                </div>
              ) : (
                <div className="gs-rows">
                  {results.map((p, i) => <PatientRow key={uuidOf(p) || p.mrn || i} {...rowProps(p, i)} />)}
                  <button className="gs-addnew" onClick={() => addFromText()} data-testid="gs-add-new">+ Add new patient</button>
                </div>
              )}
            </>
          )}
          <div className="gs-foot">
            <span className="gs-kb"><kbd>↑</kbd><kbd>↓</kbd> to move</span>
            <span className="gs-kb"><kbd>↵</kbd> opens the chart</span>
            <span className="gs-kb"><kbd>esc</kbd> to close</span>
          </div>
        </div>
      , document.body) : null}

      <ScheduleModal open={!!schedulePatient} onOpenChange={(o) => { if (!o) setSchedulePatient(null); }} patient={schedulePatient} clientId={selectedClientId} />
    </div>
  );
}
